/**
 * Magic-link verify landing — top-level redirect bridge per ADR-0018 §6.2.
 *
 * The verify flow is **not** a JS-driven API call. The dashboard's
 * `<Verify />` view runs at `app.sill.so/verify?token=...` (the email
 * link target until ADR-0011's email-template change points directly
 * at the API). On mount, the view extracts the token from the URL
 * and does a top-level navigation to
 * `${API_ORIGIN}/v1/auth/verify?token=...`. The server responds with
 * `303 + Set-Cookie: sill_session=...; Domain=.sill.so + Location:
 * https://app.sill.so/`; the browser commits the cookie (cross-
 * subdomain attach per ADR-0017 §1.4) and follows the redirect; the
 * dashboard's bootstrap auth check (`<AuthProvider>`) then sees the
 * cookie via auto-attach and hydrates `currentUser`.
 *
 * No fetch from JS, no `auth.verify()` API call, no client-side
 * session reconstruction. The view's only role is to render
 * "Verifying…" copy during the redirect hop and to surface a clear
 * error if the token is missing from the URL.
 */

import { useEffect, useState } from 'react';

import { API_ORIGIN } from '../../app/env.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export interface VerifyProps {
  setView: (v: View) => void;
}

export function Verify({ setView }: VerifyProps): JSX.Element {
  const C = useC();
  const [missingToken, setMissingToken] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const token = new URL(window.location.href).searchParams.get('token') ?? '';
    if (!token) {
      setMissingToken(true);
      return;
    }
    // Top-level navigation to the API. Server responds with
    // 303 + Set-Cookie + Location → browser follows back to the
    // dashboard root authenticated.
    window.location.assign(
      `${API_ORIGIN}/v1/auth/verify?token=${encodeURIComponent(token)}`,
    );
  }, []);

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-24 text-center">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 12,
        }}
      >
        VERIFY
      </div>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(32px, 6vw, 48px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -1.5,
          marginBottom: 16,
        }}
      >
        {missingToken ? 'Try again.' : 'One moment.'}
      </h1>
      <p style={{ fontFamily: sansFont, fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
        {missingToken
          ? 'Missing token in URL. Use the link from your email.'
          : 'Verifying your sign-in link…'}
      </p>
      {missingToken ? (
        <button
          type="button"
          onClick={() => setView('login')}
          className="mt-8 px-5 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
          }}
        >
          Back to sign-in
        </button>
      ) : null}
    </div>
  );
}
